// ===========================================
// PREMIUM HEADER - VP-Level Design
// Matching Tripnovac Platform Hub Quality
// Search, Notifications, Quick Actions
// ===========================================

import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getApiBaseUrl } from '../../lib/apiBase';
import {
  Search,
  Bell,
  Settings,
  HelpCircle,
  Command,
  Sparkles,
  Globe,
  Calendar,
  Mail,
  MessageSquare,
  Zap,
  ChevronDown,
  User,
  LogOut,
  Moon,
  Sun,
  ExternalLink,
  ArrowRight,
  X,
  Check,
  AlertCircle,
  Info,
  Plane,
  CreditCard,
  FileText,
  BarChart3,
  Target,
  Users,
  Megaphone,
} from 'lucide-react';
import { useUIStore, useAuthStore } from '../../store';
import { useNavigate } from 'react-router-dom';
import { notificationsService, InAppNotification } from '../../services/notifications.service';

interface QuickAction {
  icon: React.ReactNode;
  label: string;
  shortcut?: string;
  action: () => void;
  color: string;
}

interface Notification {
  id: string;
  type: 'success' | 'warning' | 'error' | 'info';
  title: string;
  message: string;
  time: string;
  read: boolean;
}

const PremiumHeader: React.FC = () => {
  const navigate = useNavigate();
  const { sidebarCollapsed, toggleDarkMode, darkMode } = useUIStore();
  const { user, logout } = useAuthStore();
  const [showSearch, setShowSearch] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [searchResults, setSearchResults] = useState<Array<{ type: string; name: string; subtitle: string }>>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Fetch real notifications from API
  useEffect(() => {
    const fetchNotifications = async () => {
      const token = localStorage.getItem('access_token');
      if (!token || !user) return; // Wait for auth
      try {
        const response = await notificationsService.listInApp({ status: 'all', limit: 20 });
        setNotifications(response.notifications?.map((n: InAppNotification) => ({
          id: n.id,
          type: n.type || 'info',
          title: n.title,
          message: n.body,
          time: new Date(n.createdAt).toLocaleString(),
          read: !!n.readAt,
        })) || []);
      } catch (error) {
        // No notifications available - that's fine
        setNotifications([]);
      }
    };
    fetchNotifications();
  }, [user]);

  // Real search functionality
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const searchDebounce = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const response = await fetch(`${getApiBaseUrl()}/api/search?q=${encodeURIComponent(searchQuery)}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setSearchResults(data.results || []);
        } else {
          setSearchResults([]);
        }
      } catch (error) {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(searchDebounce);
  }, [searchQuery]);

  const quickActions: QuickAction[] = [
    {
      icon: <Mail className="w-4 h-4" />,
      label: 'Compose Email',
      shortcut: 'E',
      action: () => navigate('/emails'),
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: <Users className="w-4 h-4" />,
      label: 'Add Contact',
      shortcut: 'C',
      action: () => navigate('/contacts'),
      color: 'from-violet-500 to-purple-500',
    },
    {
      icon: <Megaphone className="w-4 h-4" />,
      label: 'New Campaign',
      shortcut: 'N',
      action: () => navigate('/campaigns'),
      color: 'from-amber-500 to-orange-500',
    },
    {
      icon: <BarChart3 className="w-4 h-4" />,
      label: 'View Analytics',
      shortcut: 'A',
      action: () => navigate('/analytics'),
      color: 'from-emerald-500 to-teal-500',
    },
  ];

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
        setTimeout(() => searchInputRef.current?.focus(), 100);
      }
      if (e.key === 'Escape') {
        setShowSearch(false);
        setShowNotifications(false);
        setShowQuickActions(false);
        setShowUserMenu(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  const NotificationIcon = ({ type }: { type: string }) => {
    switch (type) {
      case 'success':
        return <Check className="w-4 h-4 text-emerald-400" />;
      case 'warning':
        return <AlertCircle className="w-4 h-4 text-amber-400" />;
      case 'error':
        return <X className="w-4 h-4 text-red-400" />;
      default:
        return <Info className="w-4 h-4 text-blue-400" />;
    }
  };

  return (
    <>
      <header
        className={`
          fixed top-0 right-0 h-16 z-40
          bg-white/80 backdrop-blur-xl border-b border-gray-200/50
          transition-all duration-300
          ${sidebarCollapsed ? 'left-20' : 'left-72'}
        `}
      >
        <div className="h-full px-6 flex items-center justify-between gap-4">
          {/* Left Section - Breadcrumb / Page Title */}
          <div className="flex items-center gap-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              onClick={() => setShowSearch(true)}
              className="flex items-center gap-3 h-10 px-4 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors group"
            >
              <Search className="w-4 h-4 text-gray-400 group-hover:text-violet-500 transition-colors" />
              <span className="text-sm text-gray-500">Search anything...</span>
              <div className="flex items-center gap-1 ml-8">
                <kbd className="px-1.5 py-0.5 text-[10px] bg-white text-gray-500 rounded shadow-sm font-mono">⌘</kbd>
                <kbd className="px-1.5 py-0.5 text-[10px] bg-white text-gray-500 rounded shadow-sm font-mono">K</kbd>
              </div>
            </motion.button>
          </div>

          {/* Right Section - Actions */}
          <div className="flex items-center gap-2">
            {/* Quick Actions Button */}
            <div className="relative">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowQuickActions(!showQuickActions)}
                className="relative flex items-center gap-2 h-10 px-4 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 text-white font-medium shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all"
              >
                <Zap className="w-4 h-4" />
                <span className="text-sm">Quick Actions</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showQuickActions ? 'rotate-180' : ''}`} />
              </motion.button>

              {/* Quick Actions Dropdown */}
              <AnimatePresence>
                {showQuickActions && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute right-0 top-full mt-2 w-72 bg-white rounded-2xl shadow-2xl border border-gray-200 p-2 z-50"
                  >
                    {quickActions.map((action, index) => (
                      <motion.button
                        key={index}
                        whileHover={{ x: 4 }}
                        onClick={() => {
                          action.action();
                          setShowQuickActions(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors group"
                      >
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${action.color} flex items-center justify-center text-white shadow-lg`}>
                          {action.icon}
                        </div>
                        <span className="flex-1 text-left font-medium text-gray-700 group-hover:text-gray-900">
                          {action.label}
                        </span>
                        {action.shortcut && (
                          <kbd className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded font-mono">
                            {action.shortcut}
                          </kbd>
                        )}
                      </motion.button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Tripnovac Suite Link */}
            <motion.a
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href={import.meta.env.VITE_SUITE_URL || 'https://tripnovac.com'}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 h-10 px-4 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 transition-all group"
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm font-medium">Suite</span>
              <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
            </motion.a>

            {/* Help Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.open('https://tripnovac.com/help', '_blank')}
              className="w-10 h-10 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 hover:text-gray-900 transition-all"
            >
              <HelpCircle className="w-5 h-5" />
            </motion.button>

            {/* Notifications */}
            <div className="relative">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative w-10 h-10 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 hover:text-gray-900 transition-all"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
                    {unreadCount}
                  </span>
                )}
              </motion.button>

              {/* Notifications Dropdown */}
              <AnimatePresence>
                {showNotifications && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute right-0 top-full mt-2 w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden z-50"
                  >
                    <div className="p-4 border-b border-gray-100">
                      <div className="flex items-center justify-between">
                        <h3 className="font-bold text-gray-900">Notifications</h3>
                        <button 
                          onClick={async () => {
                            try {
                              await notificationsService.markAllRead();
                              setNotifications(prev => prev.map(n => ({ ...n, read: true })));
                            } catch (error) {
                              console.error('Failed to mark all as read:', error);
                            }
                          }}
                          className="text-sm text-violet-600 hover:text-violet-700 font-medium"
                        >
                          Mark all read
                        </button>
                      </div>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.map((notification) => (
                        <motion.div
                          key={notification.id}
                          whileHover={{ backgroundColor: 'rgba(139, 92, 246, 0.05)' }}
                          className={`p-4 border-b border-gray-50 cursor-pointer ${!notification.read ? 'bg-violet-50/50' : ''}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              notification.type === 'success' ? 'bg-emerald-100' :
                              notification.type === 'warning' ? 'bg-amber-100' :
                              notification.type === 'error' ? 'bg-red-100' :
                              'bg-blue-100'
                            }`}>
                              <NotificationIcon type={notification.type} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className="font-semibold text-gray-900">{notification.title}</p>
                                {!notification.read && (
                                  <span className="w-2 h-2 bg-violet-500 rounded-full" />
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mt-0.5">{notification.message}</p>
                              <p className="text-xs text-gray-400 mt-1">{notification.time}</p>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                    <div className="p-3 border-t border-gray-100 bg-gray-50">
                      <button 
                        onClick={() => {
                          navigate('/notifications');
                          setShowNotifications(false);
                        }}
                        className="w-full py-2 text-sm font-medium text-violet-600 hover:text-violet-700 rounded-xl hover:bg-violet-100 transition-colors"
                      >
                        View All Notifications
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* User Menu */}
            <div className="relative">
              <motion.button
                whileHover={{ scale: 1.02 }}
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-3 h-10 pl-1 pr-3 rounded-xl hover:bg-gray-100 transition-all"
              >
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white font-bold text-sm shadow-lg">
                  {user?.firstName?.[0] || user?.email[0].toUpperCase()}
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-semibold text-gray-900">
                    {user?.firstName || 'User'}
                  </p>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
              </motion.button>

              {/* User Menu Dropdown */}
              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute right-0 top-full mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden z-50"
                  >
                    <div className="p-4 bg-gradient-to-br from-violet-500 to-purple-600">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center text-white font-bold text-lg">
                          {user?.firstName?.[0] || user?.email[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="font-bold text-white">
                            {user?.firstName} {user?.lastName}
                          </p>
                          <p className="text-sm text-white/70">{user?.email}</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-2">
                      <button
                        onClick={() => {
                          navigate('/settings');
                          setShowUserMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors"
                      >
                        <User className="w-5 h-5 text-gray-400" />
                        <span className="font-medium text-gray-700">Profile & Settings</span>
                      </button>
                      <button
                        onClick={() => {
                          navigate('/billing');
                          setShowUserMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors"
                      >
                        <CreditCard className="w-5 h-5 text-gray-400" />
                        <span className="font-medium text-gray-700">Billing</span>
                      </button>
                      <button
                        onClick={() => {
                          navigate('/notifications');
                          setShowUserMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors"
                      >
                        <Bell className="w-5 h-5 text-gray-400" />
                        <span className="font-medium text-gray-700">Notifications</span>
                      </button>
                      <button
                        onClick={() => {
                          toggleDarkMode();
                          const isDark = !darkMode;
                          if (isDark) {
                            document.documentElement.classList.add('dark');
                          } else {
                            document.documentElement.classList.remove('dark');
                          }
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors"
                      >
                        {darkMode ? <Sun className="w-5 h-5 text-gray-400" /> : <Moon className="w-5 h-5 text-gray-400" />}
                        <span className="font-medium text-gray-700">
                          {darkMode ? 'Light Mode' : 'Dark Mode'}
                        </span>
                      </button>
                      <div className="my-2 border-t border-gray-100" />
                      <button
                        onClick={() => {
                          logout();
                          setShowUserMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-red-50 text-red-600 transition-colors"
                      >
                        <LogOut className="w-5 h-5" />
                        <span className="font-medium">Logout</span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </header>

      {/* Command Palette / Global Search Modal */}
      <AnimatePresence>
        {showSearch && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowSearch(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            />
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              className="fixed top-24 left-1/2 -translate-x-1/2 w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden z-50"
            >
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <Search className="w-5 h-5 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search contacts, campaigns, templates..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 text-lg outline-none placeholder:text-gray-400"
                    autoFocus
                  />
                  <kbd className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded font-mono">
                    ESC
                  </kbd>
                </div>
              </div>

              {/* Search Results */}
              {searchQuery && (
                <div className="max-h-96 overflow-y-auto p-2">
                  {searchResults.length > 0 ? (
                    searchResults.map((result, index) => (
                      <motion.button
                        key={index}
                        whileHover={{ backgroundColor: 'rgba(139, 92, 246, 0.05)' }}
                        className="w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-colors"
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          result.type === 'Contact' ? 'bg-blue-100 text-blue-600' :
                          result.type === 'Campaign' ? 'bg-amber-100 text-amber-600' :
                          result.type === 'Template' ? 'bg-violet-100 text-violet-600' :
                          'bg-emerald-100 text-emerald-600'
                        }`}>
                          {result.type === 'Contact' && <Users className="w-5 h-5" />}
                          {result.type === 'Campaign' && <Megaphone className="w-5 h-5" />}
                          {result.type === 'Template' && <Mail className="w-5 h-5" />}
                          {result.type === 'Booking' && <Calendar className="w-5 h-5" />}
                        </div>
                        <div className="flex-1 text-left">
                          <p className="font-medium text-gray-900">{result.name}</p>
                          <p className="text-sm text-gray-500">{result.subtitle}</p>
                        </div>
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                          {result.type}
                        </span>
                      </motion.button>
                    ))
                  ) : (
                    <div className="p-8 text-center">
                      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                        <Search className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-gray-600 font-medium">No results found</p>
                      <p className="text-sm text-gray-400 mt-1">Try a different search term</p>
                    </div>
                  )}
                </div>
              )}

              {/* Quick Actions */}
              {!searchQuery && (
                <div className="p-4 border-t border-gray-100 bg-gray-50">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Quick Actions</p>
                  <div className="grid grid-cols-2 gap-2">
                    {quickActions.map((action, index) => (
                      <motion.button
                        key={index}
                        whileHover={{ scale: 1.02 }}
                        onClick={() => {
                          action.action();
                          setShowSearch(false);
                        }}
                        className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white border border-gray-200 hover:border-violet-300 hover:shadow-md transition-all"
                      >
                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${action.color} flex items-center justify-center text-white`}>
                          {action.icon}
                        </div>
                        <span className="font-medium text-gray-700">{action.label}</span>
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Click Outside Handler */}
      {(showNotifications || showQuickActions || showUserMenu) && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => {
            setShowNotifications(false);
            setShowQuickActions(false);
            setShowUserMenu(false);
          }}
        />
      )}
    </>
  );
};

export default PremiumHeader;
